import { useState } from "react";
import useUserStore from "../../states/user";
import useAvailabilityStore from "../../states/availability";
import { InsertAvailabilityException } from "../../services/AvailbilityService";
import styles from "./AvailabilityExceptions.module.css";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import { AnimatedReveal } from "@iq/models"
const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
});

function formatDate(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
}

function AvailabilityExceptions() {
    const userId = useUserStore((state) => state.session?.profile?.id);
    const exceptions = useAvailabilityStore((state) => state.exceptions);
    const fetchExceptions = useAvailabilityStore((state) => state.fetchAvailabilityExceptions);
    const [status, setStatus] = useState({ type: "", message: "" });
    const [isSaving, setIsSaving] = useState(false);
    const [startAt, setStartAt] = useState("");
    const [endAt, setEndAt] = useState("");
    const [reason, setReason] = useState("");

    const sortedExceptions = [...exceptions].sort(
        (first, second) => new Date(second.start_at).getTime() - new Date(first.start_at).getTime()
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        const trimmedReason = reason.trim();

        if (!startAt || !endAt || !trimmedReason) {
            setStatus({ type: "error", message: "Start, end, and reason are required." });
            return;
        }
        if (new Date(endAt) <= new Date(startAt)) {
            setStatus({ type: "error", message: "End must be after start." });
            return;
        }

        setIsSaving(true);
        setStatus({ type: "", message: "" });
        try {
            await InsertAvailabilityException(startAt, endAt, trimmedReason, userId);
            await fetchExceptions(userId);
            setStartAt("");
            setEndAt("");
            setReason("");
            setStatus({ type: "success", message: "Exception added." });
        } catch (error) {
            setStatus({ type: "error", message: error.message || "Could not add the exception." });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <aside className={styles.card} aria-labelledby="exceptions-title" data-reveal>
            <LoadingScreen open={isSaving} message="Saving availability exception..." />
            <header className={styles.header}>
                <h2 id="exceptions-title" >Availability exceptions</h2>
                <p>One-time changes to your regular hours.</p>
            </header>

            <form className={styles.form} onSubmit={handleSubmit}>
                <label>
                    <span>Start</span>
                    <input
                        type="datetime-local"
                        value={startAt}
                        onChange={(event) => setStartAt(event.target.value)}
                    />
                </label>
                <label>
                    <span>End</span>
                    <input
                        type="datetime-local"
                        value={endAt}
                        min={startAt || undefined}
                        onChange={(event) => setEndAt(event.target.value)}
                    />
                </label>
                <label>
                    <span>Reason</span>
                    <input
                        type="text"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="e.g. Holiday"
                        maxLength={255}
                    />
                </label>
                <button className={styles.addButton} type="submit" disabled={!userId || isSaving}>
                    {isSaving ? "Adding..." : "Add exception"}
                </button>
            </form>

            {status.message && (
                <p className={`${styles.status} ${styles[status.type]}`} role="status">
                    {status.message}
                </p>
            )}

            <div className={styles.list} aria-live="polite">
                {sortedExceptions.length > 0 ? (
                    sortedExceptions.map((exception, index) => {
                        const isPast = new Date(exception.end_at).getTime() < Date.now();
                        return (
                            <article
                                className={`${styles.item}${isPast ? ` ${styles.past}` : ""}`}
                                key={`${exception.start_at}-${exception.end_at}-${index}`}
                            >
                                <div className={styles.itemHeader}>
                                    <strong>{exception.reason || "No reason provided"}</strong>
                                    {isPast && <span className={styles.badge}>Past</span>}
                                </div>
                                <time dateTime={exception.start_at}>{formatDate(exception.start_at)}</time>
                                <span className={styles.through}>through</span>
                                <time dateTime={exception.end_at}>{formatDate(exception.end_at)}</time>
                            </article>
                        );
                    })
                ) : (
                    <div className={styles.empty}>
                        <strong>No exceptions yet</strong>
                        <span>Added exceptions will appear here.</span>
                    </div>
                )}
            </div>
        </aside>
    );
}

export default AvailabilityExceptions;